import {
    BymoPageProps,
    doppeHideableValueGetVisibleValue,
    DoppeHtmlSiteInfo,
    DoppeHtmlSitePagesType,
    doppeHtmlSiteResolveAllPostsPageUrl,
    doppeHtmlSiteResolveAllProductsPageUrl,
    doppeHtmlSiteResolveBookOnlinePageUrl,
    doppeHtmlSiteResolveCartPageUrl,
    doppeHtmlSiteResolveEventsPageURL,
    doppeHtmlSiteResolvePricingPlansPageURL
} from '../../../client-server-common';
import {bymoPageGetInnerNavigatablePageById} from '../../../client-server-common/bymo-page-utils/bymo-page-utils';
import {
    autoValueIsAutoValue,
    stringTrimPrefix,
    uiPartCreateMarking,
    validateIsEmail,
    validateIsUrl
} from '@wix/devzai-utils-common';
import {DoppeActionCtaButtonsSettings} from '../../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    DoppeActionTypeWidgetInnerCheckoutButtonParts,
    doppeActionTypeWidgetInnerCheckoutButtonPartsCreateName
} from '../../../components/doppe-action-type-widget-inner-checkout-button/doppe-action-type-widget-inner-checkout-button.ui-parts';
import {
    DoppeActionCTAButton,
    doppeActionCtaButtonLinkResolveTarget,
    doppeActionCtaButtonTypeOpenUrl,
    DoppeActionCTAType,
    doppeActionMultipleCTAButtonIsVisible,
    DoppeActionSettingsCTAButtonSettings,
    DoppeLinkSpec,
    DoppeLinkSpecEntityType,
    doppeLinkSpecIsEntity,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl
} from '../../../client-server-common/types/doppe-action-cta-button';
import {DoppeViewerLang} from '../../../client-server-common/doppe-viewer/doppe-viewer-lang';
import {DoppeViewerPagesRoutesManager} from '../../../client-server-common/viewer-pages-routes-manager/doppe-viewer-pages-routes-manager';
import {DoppeDtoInnerPageType} from '../../../client-server-common/types/doppe-dto-inner-page';
import {DoppeViewerPageType} from '../../../client-server-common/viewer-pages/doppe-viewer-pages';

/**
 * Resolves a DoppeLinkSpec to a URL string.
 * Handles URL strings, Page references, and Entity references.
 * 
 * @param linkSpec - The link specification to resolve
 * @param options - Options for URL resolution
 * @returns The resolved URL string, or undefined if cannot be resolved
 */
export function doppeLinkSpecResolveToUrl(
    linkSpec: DoppeLinkSpec,
    options: {
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
        bymoPageProps?: BymoPageProps | null;
        viewerPagesRoutesManager?: DoppeViewerPagesRoutesManager | null;
    }
): string | undefined {
    const {htmlSiteInfo, bymoPageProps, viewerPagesRoutesManager} = options;
    
    // Handle simple URL string
    if (doppeLinkSpecIsUrl(linkSpec)) {
        if (validateIsUrl(linkSpec, {protocols: ['http', 'https'], require_protocol: true})) {
            return linkSpec;
        }
        return linkSpec || undefined;
    }
    
    // Handle Page link spec - resolve URL from page info
    if (doppeLinkSpecIsPage(linkSpec) && bymoPageProps) {
        const page = bymoPageGetInnerNavigatablePageById(linkSpec.pageId, bymoPageProps);
        if (page) {
            // Use routes manager if available (viewer mode), otherwise use htmlSiteInfo (live preview)
            if (viewerPagesRoutesManager) {
                switch (page.type) {
                    case DoppeDtoInnerPageType.Products:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.ProductsList
                        });
                    case DoppeDtoInnerPageType.BlogPosts:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.BlogPostsList
                        });
                    case DoppeDtoInnerPageType.Events:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.EventsList
                        });
                    case DoppeDtoInnerPageType.Bookings:
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.ServicesList
                        });
                    case DoppeDtoInnerPageType.Content:
                    default:
                        return viewerPagesRoutesManager.contentPage({slug: page.slug});
                }
            } else if (htmlSiteInfo && page.slug) {
                // Fallback for live preview - build URL using base URL + slug
                return `${htmlSiteInfo.htmlSiteUrl}/${page.slug}`;
            }
        }
        return undefined;
    }
    
    // Handle Entity link spec - resolve URL based on entity type
    if (doppeLinkSpecIsEntity(linkSpec)) {
        const entityType = linkSpec.entityType;
        const entityId = linkSpec.entityId;
        
        if (viewerPagesRoutesManager) {
            switch (entityType) {
                case DoppeLinkSpecEntityType.Product:
                    return viewerPagesRoutesManager.resolvePath({
                        pageType: DoppeViewerPageType.ProductItem,
                        productSlug: entityId
                    });
                case DoppeLinkSpecEntityType.BlogPost:
                    return viewerPagesRoutesManager.resolvePath({
                        pageType: DoppeViewerPageType.BlogPostItem,
                        postSlug: entityId
                    });
                case DoppeLinkSpecEntityType.Event:
                    return viewerPagesRoutesManager.resolvePath({
                        pageType: DoppeViewerPageType.EventItem,
                        eventSlug: entityId
                    });
                case DoppeLinkSpecEntityType.Service:
                    return viewerPagesRoutesManager.resolvePath({
                        pageType: DoppeViewerPageType.ServiceItem,
                        serviceSlug: entityId
                    });
            }
        } else if (htmlSiteInfo) {
            // Fallback for live preview
            switch (entityType) {
                case DoppeLinkSpecEntityType.Product:
                    return `${htmlSiteInfo.htmlSiteUrl}/product/${entityId}`;
                case DoppeLinkSpecEntityType.BlogPost:
                    return `${htmlSiteInfo.htmlSiteUrl}/post/${entityId}`;
                case DoppeLinkSpecEntityType.Event:
                    return `${htmlSiteInfo.htmlSiteUrl}/event/${entityId}`;
                case DoppeLinkSpecEntityType.Service:
                    return `${htmlSiteInfo.htmlSiteUrl}/service/${entityId}`;
            }
        }
        return undefined;
    }
    
    return undefined;
}

export function doppeActionCtaButtonTypeGetLinkSettings(
    ctaButton: DoppeActionCTAButton.BaseType,
    options: {
        /**
         * The source URL - can be a string URL or a DoppeLinkSpec
         */
        sourceURL?: DoppeLinkSpec,
        actionId: string,
        innerItemId?: string,
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
        viewerLang: DoppeViewerLang;
        isLivePreviewMode: boolean;
        isMobileView: boolean;
        /**
         * The bymoPageProps for resolving page URLs from page IDs
         */
        bymoPageProps: BymoPageProps;
        /**
         * The routes manager for proper URL generation in viewer mode.
         */
        viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;
    }
) {

    const {
        type,
    } = ctaButton.settings;

    const {
        actionId,
        innerItemId,
        sourceURL,
        htmlSiteInfo,
        viewerLang,
        isMobileView,
        isLivePreviewMode,
        bymoPageProps,
        viewerPagesRoutesManager
    } = options;

    const openURL = doppeActionCtaButtonTypeOpenUrl(ctaButton.settings);

    if (openURL) {
        return {
            href: doppeActionCtaButtonTypeResolveURL(ctaButton.settings, {
                sourceURL: sourceURL,
                htmlSiteInfo: htmlSiteInfo,
                viewerLang: viewerLang,
                showMobileView: isMobileView && isLivePreviewMode,
                bymoPageProps: bymoPageProps,
                viewerPagesRoutesManager: viewerPagesRoutesManager,
            }),
            target: doppeActionCtaButtonLinkResolveTarget(ctaButton.settings),
            rel: 'noopener',
            tagName: 'a' as const,
        }
    } else if (type === DoppeActionCTAType.Checkout) {
        return {
            ...uiPartCreateMarking(DoppeActionTypeWidgetInnerCheckoutButtonParts.Root, doppeActionTypeWidgetInnerCheckoutButtonPartsCreateName(actionId, innerItemId))
        }
    } else {
        return undefined;
    }
}

export function doppeActionCtaButtonsResolveMainURL(
    ctaButtonsSettings: DoppeActionCtaButtonsSettings,
    options: {
        /**
         * The source URL - can be a string URL or a DoppeLinkSpec
         */
        sourceURL?: DoppeLinkSpec,
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
        viewerLang: DoppeViewerLang;
        isMobileView?: boolean;
        isLivePreviewMode?: boolean;
        bymoPageProps: BymoPageProps;
        viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;

    }
): string | null {

    const {
        sourceURL,
        htmlSiteInfo,
        viewerLang,
        isMobileView,
        isLivePreviewMode,
        bymoPageProps,
        viewerPagesRoutesManager
    } = options;

    for (let i = 0; i < ctaButtonsSettings.ctaButtons.length; i++) {
        const ctaButton = ctaButtonsSettings.ctaButtons[i];
        if (doppeActionMultipleCTAButtonIsVisible(ctaButton)) {
            const url = doppeActionCtaButtonTypeResolveURL(ctaButton.settings, {
                sourceURL: sourceURL,
                htmlSiteInfo: htmlSiteInfo,
                viewerLang: viewerLang,
                showMobileView: !!isMobileView && !!isLivePreviewMode,
                bymoPageProps: bymoPageProps,
                viewerPagesRoutesManager: viewerPagesRoutesManager
            });
            if (url) {
                return url;
            }
        }
    }

    return null;
}


export function doppeActionCtaButtonTypeResolveURL(
    ctaSettings: DoppeActionSettingsCTAButtonSettings,
    options: {
        /**
         * The source URL - can be a string URL or a DoppeLinkSpec
         */
        sourceURL?: DoppeLinkSpec,
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
        viewerLang: DoppeViewerLang;
        showMobileView: boolean;
        bymoPageProps: BymoPageProps;
        viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;
    }
): string | undefined {

    const {
        sourceURL,
        htmlSiteInfo,
        viewerLang,
        showMobileView,
        bymoPageProps,
        viewerPagesRoutesManager
    } = options;

    switch (ctaSettings.type) {
        case DoppeActionCTAType.Call: {
            return `tel:${ctaSettings.phone}`;
        }
        case DoppeActionCTAType.Email: {

            if (!validateIsEmail(ctaSettings.email)) {
                return undefined;
            }

            return `mailto:${ctaSettings.email}?subject=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customSubject, ''))}&body=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}`
        }
        case DoppeActionCTAType.Share:
        case DoppeActionCTAType.Link: {
            // If url is an AutoValue, use the source URL (which can be a DoppeLinkSpec)
            if (autoValueIsAutoValue(ctaSettings.url)) {
                if (sourceURL === undefined) {
                    return '';
                }
                // Resolve the sourceURL using the common function (handles DoppeLinkSpec)
                return doppeLinkSpecResolveToUrl(sourceURL, {
                    htmlSiteInfo: htmlSiteInfo,
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager
                }) ?? '';
            }
            
            // Resolve the ctaSettings.url using the common function
            return doppeLinkSpecResolveToUrl(ctaSettings.url, {
                htmlSiteInfo: htmlSiteInfo,
                bymoPageProps: bymoPageProps,
                viewerPagesRoutesManager: viewerPagesRoutesManager
            }) ?? '';
        }
        case DoppeActionCTAType.IsracardPay: {

            const url = ctaSettings.url;

            if (validateIsUrl(url, {
                protocols: ['http', 'https'],
                require_protocol: true
            })) {
                return url;
            } else {
                return '';
            }
        }
        case DoppeActionCTAType.OpenPage: {
            if (htmlSiteInfo) {
                switch (ctaSettings.pageType) {
                    case DoppeHtmlSitePagesType.Store: {
                        return doppeHtmlSiteResolveAllProductsPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.BookingServices: {
                        return doppeHtmlSiteResolveBookOnlinePageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Cart: {
                        return doppeHtmlSiteResolveCartPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Blog: {
                        return doppeHtmlSiteResolveAllPostsPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.PricingPlans: {
                        return doppeHtmlSiteResolvePricingPlansPageURL(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Events: {
                        return doppeHtmlSiteResolveEventsPageURL(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    default: {
                        return undefined;
                    }
                }
            }

            return undefined
        }
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan: {
            // Resolve sourceURL using the common function (handles DoppeLinkSpec)
            if (sourceURL === undefined) {
                return '';
            }
            return doppeLinkSpecResolveToUrl(sourceURL, {
                htmlSiteInfo: htmlSiteInfo,
                bymoPageProps: bymoPageProps,
                viewerPagesRoutesManager: viewerPagesRoutesManager
            }) ?? '';
        }
        case DoppeActionCTAType.Whatsapp: {
            return `https://api.whatsapp.com/send?phone=${stringTrimPrefix(ctaSettings.phone, '+')}&text=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}`;
        }
        case DoppeActionCTAType.Navigate: {
            if (!ctaSettings.address) {
                return undefined;
            }

            return `https://www.google.com/maps/dir/?api=1&destination=${encodeURI(ctaSettings.address)}`
        }
        case DoppeActionCTAType.FacebookMessage: {
            return ctaSettings.userName !== undefined ?
                `https://www.m.me/${ctaSettings.userName}?text=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}` :
                undefined;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return ctaSettings.userName !== undefined ?
                `https://ig.me/m/${ctaSettings.userName}` :
                undefined;
        }
        case DoppeActionCTAType.Checkout:
        case DoppeActionCTAType.SaveContact: {
            return undefined;
        }
    }
}


export function doppeCtaButtonIsOpenInnerPageOfVertical(ctaButton: DoppeActionCTAButton.BaseType): boolean {


    switch (ctaButton.settings.type) {
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPage: {
            return true;
        }
        default:
            return false;
    }
}
